import React, {useEffect, useMemo, useState} from "react"
import PropTypes from "prop-types";

import groupBy from "../../../utils/groupBy"

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EuroIcon from "@mui/icons-material/Euro";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";


export const serviceState = {
    present: 0,
    withFee: 1,
    absent: 2,
}

const ServiceList = (props) => {
    const {
        services,
        onSave,
        getServiceState,
        isLoading,
        sortGroups = () => 0,
        renderGroup = () => "",
    } = props;

    // This is the list of services is used as source of data and keep track of the state of each service
    const [list, setList] = useState([]);

    // Create an array of [group, list_of_services] arrays, used to print the services
    const groupedList = useMemo(
        () => {
            // Group according to the group attribute
            const groups = groupBy(
                list.map((s, i) => ({...s, index: i})), // list of service with the additional index
                (s) => s.group
            )
            // sort the groups and the services and return the result
            return Object.entries(groups)
                .map(([group, list]) => [group, list.sort(
                    (serviceA, serviceB) => serviceA.name.localeCompare(serviceB.name))])
                .sort(
                    ([groupA,], [groupB,]) => sortGroups(groupA, groupB))
        }
        ,
        [list, sortGroups]
    )

    useEffect(
        () => setList(services.map((s) => {
            const state = getServiceState(s);
            return {...s, state: state, initialState: state}  // Add the initial state
        })),
        [services, getServiceState]
    )

    const handleClick = (index) => {
        if (!isLoading) {
            const service = list[index];
            const state = (service.state + 1) % 3;
            setList(list.map(s => s.id === service.id ? {...service, state: state} : s))
        }
    }

    const changesCount = list.reduce((acc, s) => s.initialState !== s.state ? acc + 1 : acc, 0);

    return <>
        <Typography color='text.primary' sx={{ml: 2, mt: 1}}>
            Clicca su un servizio per modificare il suo stato (presente, a pagamento, non presente).
            Al termine ricordati di salvare le modifiche
        </Typography>

        <Stack direction="row"
               justifyContent="space-between"
               alignItems="center"
               flexGrow={1}
               spacing={2}
               sx={{m: 2, p: 1, borderRadius: 0.5, backgroundColor: `rgb(229, 246, 253)`}}>

            <Typography variant="body2" color="text.primary">
                {changesCount === 1 ? "1 servizio modificato" : `${changesCount} servizi modificati`}
            </Typography>
            <LoadingButton onClick={() => onSave(list)}
                           disabled={changesCount === 0}
                           loading={!!isLoading}
                           variant="contained">
                Salva
            </LoadingButton>
        </Stack>

        {
            groupedList.map(([group, list]) => (
                <div key={group}>
                    {groupedList.length > 1 && renderGroup(group)}
                    <List>
                        {
                            list.map(({name, state, index}, i) => (
                                <div key={name}>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleClick(index)}>
                                            <ListItemIcon>
                                                {
                                                    state === serviceState.present ?
                                                        <CheckCircleIcon color="success"/>
                                                        : (state === serviceState.withFee ?
                                                            <EuroIcon color="warning"/>
                                                            : <CloseIcon color="disabled"/>)
                                                }
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${name} ${state === serviceState.withFee ? 'a pagamento' : ''}`}
                                                primaryTypographyProps={{color: state === serviceState.absent ? 'text.primary' : 'text.primary'}}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    {i !== list.length - 1 && <Divider variant="inset"/>}
                                </div>
                            ))
                        }
                    </List>
                </div>
            ))
        }
    </>
}

ServiceList.propTypes = {
    services: PropTypes.arrayOf(PropTypes.object).isRequired,  // A list of object with a name attribute and a group attribute
    onSave: PropTypes.func.isRequired, // Called when saving the changes passing as argument the list of services with an additional state attribute
    getServiceState: PropTypes.func.isRequired,  // A function that given a service return a possible ServiceState
    sortGroups: PropTypes.func, // A function that given two group return -1 0 or 1
    renderGroup: PropTypes.func,  // A function that given a group return the component uses as title for the group
    isLoading: PropTypes.bool,
};

export default ServiceList;